/* eslint-disable react/jsx-props-no-spreading */
import { FunctionalComponent, h } from 'preact';
import VirtualList, { ScrollDirection, Props as VirtualListProps } from './TinyVirtualList';
import { forwardRef, memo } from 'preact/compat';
import { useCallback, useLayoutEffect, useState } from 'preact/hooks';
import { RefContainer } from './VirtualList.style';
import { SECOND_LAYER_VARIANT } from '@usercentrics/cmp-browser-sdk';

import { breakpoints } from '../../styles';
import { useDeviceState } from '../../hooks';
import { useUiContext } from '../../contexts/UiContext';

interface Props extends Omit<VirtualListProps, 'height'> {
  isVisible: boolean;
  overscanCount?: number;
  scrollDirection?: ScrollDirection;
  scrollToIndex?: number;
}
const VirtualListWrapper: FunctionalComponent<Props> = forwardRef<HTMLDivElement, Props>(
  (
    {
      estimatedItemSize = 50,
      isVisible,
      overscanCount = 15,
      scrollDirection = ScrollDirection.VERTICAL,
      scrollToIndex,
      ...props
    },
    virtualListRef,
  ) => {
    const { secondLayerVariant, footerHeight } = useUiContext();
    const maxParentHeight = 300;
    const minParentHeight = 50;
    const [parentHeight, setHeight] = useState(maxParentHeight);
    const { isMobile: isMobileDevice } = useDeviceState();

    const updateHeight = useCallback(
      (height: number) => {
        let newHeight = height;

        if (height < minParentHeight) {
          return; // no need to update
        }

        if (isVisible && virtualListRef.current) {
          let toSubtractFromTop = 0;

          if (!isMobileDevice) {
            const { top } = virtualListRef?.current?.getBoundingClientRect();
            toSubtractFromTop = top;
          }

          let availableHeight = Math.round(window.innerHeight - toSubtractFromTop - footerHeight);

          if (secondLayerVariant === SECOND_LAYER_VARIANT.SIDE || isMobileDevice) {
            newHeight = availableHeight;
          } else {
            if (window.innerWidth >= breakpoints.md) {
              availableHeight -= Math.round(window.innerWidth * 0.05);
            }

            if (availableHeight > maxParentHeight || height > availableHeight) {
              availableHeight = maxParentHeight;
            }

            if (height > availableHeight) {
              newHeight = availableHeight;
            } else if (height < minParentHeight) {
              newHeight = minParentHeight;
            }
          }

          if (newHeight !== parentHeight) {
            setHeight(newHeight);
          }
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [footerHeight, isMobileDevice, isVisible, parentHeight, secondLayerVariant],
    );

    useLayoutEffect(() => {
      let virtualListHeight = 0;

      if (virtualListRef.current?.firstElementChild?.firstElementChild) {
        try {
          const listElement = virtualListRef.current.firstElementChild.firstElementChild as HTMLElement;

          virtualListHeight = parseInt(listElement.style.height.replace('px', ''), 10);
        } catch (e) {
          // no worries
        }
      }

      updateHeight(virtualListHeight);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateHeight]);

    return (
      <RefContainer ref={virtualListRef}>
        {isVisible && (
          <VirtualList
            estimatedItemSize={estimatedItemSize}
            height={parentHeight}
            overscanCount={overscanCount}
            scrollDirection={scrollDirection}
            scrollToIndex={scrollToIndex}
            {...props}
          />
        )}
      </RefContainer>
    );
  },
);

export default memo(VirtualListWrapper);
